import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import ProviderReviews from './ProviderReviews';
import JobFeed from './JobFeed';

const ProviderDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    category: 'Installation',
    price: '',
    priceType: 'per hour',
    features: '',
    availability: '',
    responseTime: ''
  });

  useEffect(() => {
    checkSubscriptionStatus();
    fetchProviderServices();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const status = await response.json();
        setSubscriptionStatus(status);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchProviderServices = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const allServices = await response.json();
        // Filter services by current provider
        const myServices = allServices.filter(service => service.providerId === user?.id);
        setServices(myServices);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: "Error",
        description: "Failed to load your services.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createService = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('dcTech_token');
      const serviceData = {
        ...newService,
        price: parseFloat(newService.price),
        features: newService.features.split(',').map(f => f.trim()).filter(f => f),
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serviceData),
      });

      if (response.ok) {
        toast({
          title: "Service created",
          description: "Your new service has been added successfully.",
        });
        
        // Reset form
        setNewService({
          title: '',
          description: '',
          category: 'Installation',
          price: '',
          priceType: 'per hour',
          features: '',
          availability: '',
          responseTime: ''
        });
        
        // Refresh services list
        fetchProviderServices();
      } else {
        throw new Error('Failed to create service');
      }
    } catch (error) {
      console.error('Error creating service:', error);
      toast({
        title: "Error",
        description: "Failed to create service. Please try again.",
        variant: "destructive",
      });
    }
  };

  const categories = [
    "Installation",
    "Maintenance",
    "Repair",
    "Smart Systems",
    "Emergency Services",
    "Inspection",
    "Plumbing Installation",
    "Pipe Repair",
    "Drain Cleaning",
    "Water Heater Service",
    "HVAC Installation",
    "Air Conditioning Repair",
    "Heating Maintenance",
    "Duct Cleaning",
    "Roofing Installation",
    "Roof Repair",
    "Gutter Cleaning",
    "Carpentry",
    "Flooring Installation",
    "Tile Work",
    "Painting",
    "Landscaping",
    "Tree Service",
    "Electrical Engineering",
    "Mechanical Engineering",
    "Civil Engineering",
    "Industrial Engineering",
    "Structural Engineering",
    "Computer Engineering",
    "Software Engineering",
    "Interior Design",
    "Architectural Design",
    "Engineering Consultation",
    "System Design",
    "Project Management",
    "Drywall Installation",
    "Drywall Repair",
    "Texturing",
    "Taping & Mudding",
    "Real Estate Sales",
    "Real Estate Consulting",
    "Property Management",
    "Home Staging"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading provider dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-white/80">Provider: {user?.name}</span>
              
              {/* Direct Navigation Dropdown - Consistent Style */}
              <div className="relative group">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Navigate
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible  z-50">
                  <div className="py-2">
                    <Link to="/dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🏠 Main Dashboard
                    </Link>
                    <Link to="/inbox" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      📨 Messages
                    </Link>
                    <Link to="/profile" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      👤 Profile
                    </Link>
                    {(user?.userType === 'admin' || user?.userType === 'supervisor') && (
                      <Link to={user?.userType === 'admin' ? '/admin' : '/supervisor-dashboard'} className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        {user?.userType === 'admin' ? '⚙️ Admin Panel' : '👥 Supervisor Panel'}
                      </Link>
                    )}
                    {user?.userType === 'admin' && (
                      <Link to="/supervisor-dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        👥 Supervisor View
                      </Link>
                    )}
                    <div className="border-t border-white/20 my-2"></div>
                    <button 
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-red-400 bg-red-500/20 transition-colors"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Provider Dashboard</h1>
          <p className="text-xl text-white/70">
            Manage your services and bookings across multiple trades
          </p>
        </div>

        {/* Subscription Status */}
        {!subscriptionLoading && (
          <div className="mb-8">
            {subscriptionStatus?.status === 'active' ? (
              <div className="bg-green-600/20 border border-green-400/30 backdrop-blur-lg rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-300 font-semibold">
                      Subscription Active - {subscriptionStatus.plan}
                    </span>
                  </div>
                  <Link to="/subscription">
                    <Button variant="outline" size="sm" className="border-green-400/50 text-green-300 hover:bg-green-400/10">
                      Manage Subscription
                    </Button>
                  </Link>
                </div>
                <p className="text-green-200/80 mt-2 text-sm">
                  Expires: {new Date(subscriptionStatus.expiresAt).toLocaleDateString()}
                </p>
              </div>
            ) : (
              <div className="bg-orange-600/20 border border-orange-400/30 backdrop-blur-lg rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                    <span className="text-orange-300 font-semibold">
                      Subscription Required
                    </span>
                  </div>
                  <Link to="/subscription">
                    <Button className="bg-gradient-to-r from-orange-500 to-red-500   text-white">
                      Subscribe Now
                    </Button>
                  </Link>
                </div>
                <p className="text-orange-200/80 mt-2 text-sm">
                  Subscribe to create services, manage bookings, and access all provider features.
                </p>
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="services" className="w-full">
          <TabsList className="grid w-full grid-cols-5 bg-slate-800/80 mb-8">
            <TabsTrigger value="services" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              ⚡ Job History
            </TabsTrigger>
            <TabsTrigger value="jobs" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              💼 Available Jobs
            </TabsTrigger>
            <TabsTrigger value="bookings" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              📅 Manage Bookings
            </TabsTrigger>
            <TabsTrigger value="reviews" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              ⭐ Reviews
            </TabsTrigger>
            <TabsTrigger value="subscription" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              💳 Subscription
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <span>⚡</span>
                  <span>Job History - Completed Services</span>
                </CardTitle>
                <p className="text-white/70">View your completed job history and service records</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Filter/Search bar */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <input
                      type="text"
                      placeholder="Search completed jobs..."
                      className="flex-1 bg-white/10 border border-white/30 rounded-md px-4 py-2 text-white placeholder-white/50"
                    />
                    <select className="bg-white/10 border border-white/30 rounded-md px-4 py-2 text-white">
                      <option value="all" className="bg-slate-800">All Services</option>
                      <option value="last30days" className="bg-slate-800">Last 30 Days</option>
                      <option value="last90days" className="bg-slate-800">Last 90 Days</option>
                      <option value="thisyear" className="bg-slate-800">This Year</option>
                    </select>
                  </div>

                  {/* Job History List */}
                  <div className="space-y-4">
                    {/* Sample completed job entries */}
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg">Residential Electrical Panel Upgrade</h3>
                          <p className="text-white/70 text-sm">Customer: John Smith</p>
                          <p className="text-white/60 text-sm">Location: 123 Main St, Springfield</p>
                        </div>
                        <div className="text-right">
                          <span className="bg-green-600/20 text-green-300 px-3 py-1 rounded-full text-sm font-medium">
                            Completed
                          </span>
                          <p className="text-white/60 text-sm mt-1">Nov 15, 2024</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-yellow-400">⭐⭐⭐⭐⭐ (5.0)</span>
                          <span className="text-white/60">Duration: 6 hours</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-semibold text-lg">$850.00</span>
                          <p className="text-white/60 text-sm">Paid: Nov 16, 2024</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg">Kitchen Outlet Installation</h3>
                          <p className="text-white/70 text-sm">Customer: Maria Garcia</p>
                          <p className="text-white/60 text-sm">Location: 456 Oak Ave, Downtown</p>
                        </div>
                        <div className="text-right">
                          <span className="bg-green-600/20 text-green-300 px-3 py-1 rounded-full text-sm font-medium">
                            Completed
                          </span>
                          <p className="text-white/60 text-sm mt-1">Nov 12, 2024</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-yellow-400">⭐⭐⭐⭐⭐ (5.0)</span>
                          <span className="text-white/60">Duration: 3 hours</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-semibold text-lg">$425.00</span>
                          <p className="text-white/60 text-sm">Paid: Nov 13, 2024</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg">Emergency Electrical Repair</h3>
                          <p className="text-white/70 text-sm">Customer: Robert Johnson</p>
                          <p className="text-white/60 text-sm">Location: 789 Pine St, Westside</p>
                        </div>
                        <div className="text-right">
                          <span className="bg-green-600/20 text-green-300 px-3 py-1 rounded-full text-sm font-medium">
                            Completed
                          </span>
                          <p className="text-white/60 text-sm mt-1">Nov 8, 2024</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-yellow-400">⭐⭐⭐⭐⭐ (4.8)</span>
                          <span className="text-white/60">Duration: 2 hours</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-semibold text-lg">$320.00</span>
                          <p className="text-white/60 text-sm">Paid: Nov 8, 2024</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 pt-6 border-t border-white/20">
                    <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-300 mb-1">23</div>
                      <div className="text-blue-200 text-sm">Jobs Completed</div>
                    </div>
                    <div className="bg-green-600/20 border border-green-400/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-300 mb-1">$8,450</div>
                      <div className="text-green-200 text-sm">Total Earnings</div>
                    </div>
                    <div className="bg-yellow-600/20 border border-yellow-400/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-300 mb-1">4.9</div>
                      <div className="text-yellow-200 text-sm">Average Rating</div>
                    </div>
                  </div>

                  {/* Note about service creation */}
                  <div className="bg-orange-600/20 border border-orange-400/30 rounded-lg p-4 mt-6">
                    <p className="text-orange-200 text-sm">
                      <strong>ℹ️ Note:</strong> Service creation is managed by administrators and supervisors. 
                      Focus on providing excellent service to your customers to maintain high ratings and increase your earnings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <span>💼</span>
                  <span>Available Jobs - Find Work</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <JobFeed />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bookings">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-8 text-center">
                <div className="text-6xl mb-6">📅</div>
                <h2 className="text-2xl font-bold text-white mb-4">Booking Management</h2>
                <p className="text-white/70 mb-6">
                  Manage all your service bookings, approve requests, and track payments.
                </p>
                <Link to="/provider-bookings">
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                    Manage Bookings
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <span>💳</span>
                  <span>Subscription Management</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-white">
                {subscriptionLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                    <p>Loading subscription information...</p>
                  </div>
                ) : subscriptionStatus?.status === 'active' ? (
                  <div className="space-y-6">
                    <div className="bg-green-600/20 border border-green-400/30 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-green-300 mb-2">Active Subscription</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-green-200/80">Plan:</span>
                          <span className="text-green-300 ml-2 font-semibold">
                            {subscriptionStatus.plan === 'basic_monthly' ? 'Basic Monthly' : 'Premium Monthly'}
                          </span>
                        </div>
                        <div>
                          <span className="text-green-200/80">Status:</span>
                          <span className="text-green-300 ml-2 font-semibold capitalize">{subscriptionStatus.status}</span>
                        </div>
                        <div>
                          <span className="text-green-200/80">Next Billing:</span>
                          <span className="text-green-300 ml-2 font-semibold">
                            {new Date(subscriptionStatus.expiresAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-4">
                      <Link to="/subscription">
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                          View All Plans
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-orange-600/20 border border-orange-400/30 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-orange-300 mb-2">No Active Subscription</h3>
                      <p className="text-orange-200/80 mb-4">
                        You need an active subscription to access provider features like creating services and managing bookings.
                      </p>
                      <Link to="/subscription">
                        <Button className="bg-gradient-to-r from-orange-500 to-red-500   text-white">
                          Choose a Plan
                        </Button>
                      </Link>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <h4 className="font-semibold mb-3">What you get with a subscription:</h4>
                      <ul className="space-y-2 text-sm text-white/80">
                        <li className="flex items-center space-x-2">
                          <span className="text-green-400">✓</span>
                          <span>Create unlimited services across all trades</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-400">✓</span>
                          <span>Manage booking requests from customers</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-400">✓</span>
                          <span>Accept secure payments through our platform</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="text-green-400">✓</span>
                          <span>Build your reputation with customer reviews</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <ProviderReviews />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProviderDashboard;